import { Suspense, useCallback, useMemo } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import NotificationsBell from '@/components/retail/NotificationsBell'
import { SkipLink, LoadingState } from '@/components/ui'
import { useToast } from '@/components/feedback/ToastProvider'
import type { NotificationItem } from '@services/notificationsApi'
import { useAuth } from '@context/AuthContext'
import {
  RetailAppShell,
  RetailPageHeader,
  useRetailNav,
  type RetailNavSection
} from '@portals/shared/components/RetailAppShell'
import OtpVerificationNotice from '@components/security/OtpVerificationNotice'
import './OperationsPortal.css'

/**
 * KAN-2035 — the control tower's rail, grouped as the approved mock
 * (Fulfilment / Network). The bespoke 280px `<aside class="operations-sidebar">` and
 * its hand-rolled drawer toggle are gone: <DhruvantaAppShell> renders the structured
 * rail and folds it into its own accessible mobile drawer below 880px.
 */
const NAV_SECTIONS: RetailNavSection[] = [
  {
    id: 'fulfilment',
    label: 'Fulfilment',
    items: [
      { to: '/', label: 'Live orders', end: true },
      { to: '/order-exceptions', label: 'Exceptions' },
      { to: '/workflow', label: 'Workflow tasks' }
    ]
  },
  {
    id: 'network',
    label: 'Network',
    items: [
      { to: '/onboarding', label: 'Onboarding queue' },
      { to: '/account-security', label: 'Account security' }
    ]
  }
]

export function OperationsShell() {
  const { authState, logout } = useAuth()
  const navigate = useNavigate()
  const { pushToast } = useToast()
  const nav = useRetailNav(NAV_SECTIONS)

  const operationsDomain =
    import.meta.env.VITE_OPERATIONS_PORTAL_DOMAIN ?? 'operations.retail.dhruvantasystems.com'

  const handleLogout = useCallback(async () => {
    await logout()
    navigate('/login')
  }, [logout, navigate])

  const handleNotificationToast = useCallback(
    (notification: NotificationItem) => {
      pushToast({
        variant: 'info',
        message: notification.title,
        description: notification.body
      })
    },
    [pushToast]
  )

  const topbarEnd = useMemo(
    () => (
      <>
        <NotificationsBell
          autoRefreshMs={60_000}
          theme="dark"
          onNewNotification={handleNotificationToast}
        />
        <button type="button" className="rt-btn rt-btn--ghost rt-btn--sm" onClick={handleLogout}>
          Logout
        </button>
        <span className="rt-avatar" aria-hidden="true">
          OP
        </span>
      </>
    ),
    [handleLogout, handleNotificationToast]
  )

  return (
    <RetailAppShell
      role="Operations"
      nav={nav}
      navAriaLabel="Operations control tower"
      searchPlaceholder="Search order / rider / seller…"
      topbarEnd={topbarEnd}
    >
      <SkipLink targetId="operations-main" />
      <div id="operations-main" className="operations-content rt-content" tabIndex={-1}>
        <RetailPageHeader
          eyebrow="Operations"
          title="Operations Control Tower"
          subtitle={
            <>
              <span className="operations-domain">{operationsDomain}</span> · monitoring
              onboarding, workflow escalations, and delivery health
            </>
          }
          actions={<span className="rt-chip">{authState.user?.name ?? 'Operations User'}</span>}
        />
        <OtpVerificationNotice
          portal="operations"
          profilePath="/account-security"
          className="operations-otp-notice"
        />
        {/* KAN-1181: the routed board components are React.lazy chunks — the Outlet needs a
            Suspense boundary so their first load shows a fallback instead of throwing. */}
        <Suspense fallback={<LoadingState label="Loading…" />}>
          <Outlet />
        </Suspense>
      </div>
    </RetailAppShell>
  )
}
